import { useRef, useState } from "react";
import { canonicalPhone, clientIdentityKey } from "./clientIdempotency.mjs";
import { MANUAL_SALE_CUTOFF_NOTICE } from "./manualSaleCutoff.mjs";
import { VoiceDictationButton } from "./VoiceDictation";

const dateInputValue = value => {
  if (!value) return "";
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

const liftSaleDateInputValue = value => {
  if (!value) return "";
  const text = String(value).trim();
  if (!/(?:T|\s)\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/i.test(text)) {
    return dateInputValue(text);
  }
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone:"America/Sao_Paulo",
    year:"numeric",
    month:"2-digit",
    day:"2-digit",
  }).formatToParts(date);
  const part = type => parts.find(item => item.type === type)?.value || "";
  return `${part("year")}-${part("month")}-${part("day")}`;
};

const draftProducts = initialValues => {
  const products = initialValues.product ?? initialValues.products ?? initialValues.produtos ?? initialValues.produto;
  if (Array.isArray(products)) return products.join(", ");
  return products == null ? "" : String(products);
};

function ClientRegistrationModal({
  onClose,
  onAdd,
  consultantId,
  storeId,
  currentUser,
  storeConsultants,
  theme: T,
  stages: STAGES,
  origins: ORIGINS,
  canManuallyRegisterSale,
  initialValues = {},
  liftOrderMode = false,
}) {
  const today = new Date().toISOString().split("T")[0];
  const liftDateSource = initialValues.dataVenda
    || initialValues.closedDate
    || initialValues.createdAt
    || initialValues.data_fechamento
    || initialValues.visitDate;
  const orderDate = liftOrderMode
    ? (liftSaleDateInputValue(initialValues.saleDay) || liftSaleDateInputValue(liftDateSource))
    : dateInputValue(initialValues.visitDate || initialValues.dataVenda || initialValues.closedDate);
  const [closedDate] = useState(() => (
    liftOrderMode ? (orderDate || today) : (dateInputValue(initialValues.closedDate) || orderDate || today)
  ));
  const isStageClosed = initialValues.stageclosed === true
    || initialValues.stageclosed === "true"
    || initialValues.stage === "closed"
    || initialValues.stage === "stageclosed";
  const [form, setForm] = useState(() => ({
    name:initialValues.name || initialValues.clientName || initialValues.cliente?.nome || "",
    phone:initialValues.phone || initialValues.telefone || initialValues.cliente?.telefone || "",
    product:draftProducts(initialValues),
    origin:initialValues.origin || ORIGINS[0],
    value:initialValues.value ?? initialValues.valorTotal ?? initialValues.valor ?? "",
    note:initialValues.note || "",
    followUp:dateInputValue(initialValues.followUp || initialValues.followUpDate),
    birthday:dateInputValue(initialValues.birthday),
    stage:isStageClosed ? "closed" : (initialValues.stage || "waiting"),
    visitDate:orderDate || today,
  }));
  const [origemCliente, setOrigemCliente] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const submittedRef = useRef(false);
  const setF = (key, value) => setForm(f => ({ ...f, [key]:value }));
  const isManagerOrAdmin = currentUser && currentUser.role !== "consultant";

  const draftSellerId = initialValues.sellerId
    ?? initialValues.consultantId
    ?? initialValues.assignedConsultantId
    ?? initialValues.responsavelId
    ?? initialValues.vendidoPor?.consultantId
    ?? initialValues.vendidoPor?.id;
  const [selectedConsultantId, setSelectedConsultantId] = useState(() => (
    liftOrderMode
      ? (draftSellerId ?? consultantId)
      : (isManagerOrAdmin ? (storeConsultants?.[0]?.id || consultantId) : consultantId)
  ));
  const finalConsultantId = liftOrderMode
    ? (draftSellerId ?? selectedConsultantId ?? consultantId)
    : (isManagerOrAdmin ? selectedConsultantId : consultantId);
  const finalStoreId = liftOrderMode
    ? (initialValues.storeId ?? storeId)
    : (isManagerOrAdmin
      ? (storeConsultants?.find(u => u.id === Number(selectedConsultantId))?.storeId || storeId)
      : storeId);
  const seller = (storeConsultants || []).find(user => String(user.id) === String(finalConsultantId));
  const sellerName = initialValues.sellerName
    || initialValues.vendidoPor?.nome
    || seller?.name
    || (finalConsultantId ? `Consultor #${finalConsultantId}` : "Não informado");
  const storeName = initialValues.storeName
    || initialValues.loja
    || (finalStoreId ? `Loja ${finalStoreId}` : "Não informada");
  const salesStages = ["closed", "posVenda", "nextSale"];
  const locked = Boolean(liftOrderMode);

  const fieldStyle = {
    width:"100%",
    padding:"8px 12px",
    borderRadius:8,
    border:`1px solid ${T.sandDark}`,
    fontSize:14,
    boxSizing:"border-box",
    background:locked ? "#F3EFE6" : "#fff",
    color:T.text,
  };
  const labelStyle = { fontSize:12, fontWeight:700, color:T.text, display:"block", marginBottom:4 };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(15,31,61,0.6)", zIndex:100, display:"flex", alignItems:"flex-start", justifyContent:"center", padding:20, overflowY:"auto" }}>
      <div style={{ background:"#fff", borderRadius:18, padding:28, width:"100%", maxWidth:440, boxShadow:"0 20px 60px rgba(0,0,0,0.25)", margin:"auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div style={{ fontWeight:800, fontSize:18, color:T.text }}>{liftOrderMode ? "Pedido LIFT" : "Novo Cliente"}</div>
          <button type="button" onClick={onClose} disabled={saving} aria-label="Fechar" style={{ background:T.sandDark, border:"none", borderRadius:99, width:32, height:32, cursor:saving?"default":"pointer", fontSize:16, opacity:saving?0.5:1 }}>✕</button>
        </div>
        {liftOrderMode && (
          <div style={{ background:"#F3EFE6", border:`1px solid ${T.sandDark}`, borderRadius:10, padding:"10px 12px", marginBottom:14, fontSize:13, color:T.text }}>
            <div><strong>Loja:</strong> {storeName} {finalStoreId ? <span>(#{finalStoreId})</span> : null}</div>
            <div style={{ marginTop:4 }}><strong>Pedido LIFT:</strong> #{initialValues.liftPedidoId || "—"}</div>
          </div>
        )}
        {[
          ["Nome completo","name","text"],
          ["Contato (WhatsApp / Telefone)","phone","tel"],
          ["Valor do orçamento (R$)","value","text"],
        ].map(([label,key,type]) => (
          <div key={key} style={{ marginBottom:12 }}>
            <label htmlFor={`client-registration-${key}`} style={labelStyle}>{label}</label>
            <input id={`client-registration-${key}`} type={type} value={form[key]} readOnly={locked} onChange={e => setF(key,e.target.value)} style={fieldStyle} />
          </div>
        ))}
        <div style={{ marginBottom:12 }}>
          <label htmlFor="client-registration-product" style={labelStyle}>Produto(s) de interesse</label>
          <input id="client-registration-product" value={form.product} readOnly={locked} onChange={e=>setF("product",e.target.value)} placeholder="Ex: sofá e mesa" style={fieldStyle} />
        </div>
        {!liftOrderMode && (
          <div style={{ marginBottom:12 }}>
            <label htmlFor="client-registration-origin" style={labelStyle}>Origem</label>
            <select id="client-registration-origin" value={form.origin} onChange={e=>setF("origin",e.target.value)} style={fieldStyle}>
              {ORIGINS.map(o=><option key={o}>{o}</option>)}
            </select>
          </div>
        )}
        {liftOrderMode && (
          <div style={{ marginBottom:12 }}>
            <label htmlFor="client-registration-lift-origin" style={labelStyle}>De onde veio este cliente?</label>
            <select
              id="client-registration-lift-origin"
              value={origemCliente}
              onChange={e => setOrigemCliente(e.target.value)}
              style={fieldStyle}
            >
              <option value="">Selecione uma opção</option>
              <option value="lead">Lead (anúncio)</option>
              <option value="porta">Porta (presencial)</option>
            </select>
          </div>
        )}
        <div style={{ marginBottom:12 }}>
          {liftOrderMode ? (
            <>
              <label style={labelStyle}>Consultor Responsável</label>
              <div aria-label="Consultor Responsável" style={{ ...fieldStyle, background:"#F3EFE6" }}>{sellerName}</div>
            </>
          ) : (
            <>
              <label htmlFor="client-registration-consultant" style={labelStyle}>Consultor Responsável</label>
              {isManagerOrAdmin ? (
                <select id="client-registration-consultant" value={selectedConsultantId} onChange={e=>setSelectedConsultantId(Number(e.target.value))} style={fieldStyle}>
                  {(storeConsultants||[]).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              ) : (
                <div style={{ ...fieldStyle, background:"#F3EFE6", color:T.muted }}>
                  {currentUser?.name} <span style={{ fontSize:11 }}>(você — quem inicia o atendimento é o responsável)</span>
                </div>
              )}
            </>
          )}
        </div>
        <div style={{ marginBottom:12 }}>
          <label htmlFor="client-registration-stage" style={labelStyle}>Etapa no Atendimento</label>
          <select id="client-registration-stage" value={form.stage} disabled={locked} onChange={e => setF("stage",e.target.value)} style={{ ...fieldStyle, background:locked?"#F3EFE6":"#fff" }}>
            {STAGES.filter(s => liftOrderMode
              || canManuallyRegisterSale(currentUser)
              || !salesStages.includes(s.id))
              .map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div style={{ marginBottom:12 }}>
          <label htmlFor="client-registration-visit-date" style={labelStyle}>📅 {liftOrderMode ? "Data da venda" : "Data do atendimento"}</label>
          <input id="client-registration-visit-date" type="date" value={form.visitDate||today} max={locked ? undefined : today} readOnly={locked} disabled={locked} onChange={e => setF("visitDate", e.target.value > today ? today : e.target.value)} style={fieldStyle} />
        </div>
        {liftOrderMode && (
          <div style={{ marginBottom:12 }}>
            <label htmlFor="client-registration-closed-date" style={labelStyle}>Data de fechamento</label>
            <input id="client-registration-closed-date" type="date" value={closedDate} readOnly disabled style={fieldStyle} />
          </div>
        )}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
          <div>
            <label htmlFor="client-registration-follow-up" style={labelStyle}>Follow-up</label>
            <input id="client-registration-follow-up" type="date" value={form.followUp} onChange={e => setF("followUp",e.target.value)} style={fieldStyle} />
          </div>
          <div>
            <label htmlFor="client-registration-birthday" style={labelStyle}>🎂 Aniversário</label>
            <input id="client-registration-birthday" type="date" value={form.birthday||""} onChange={e=>setF("birthday",e.target.value)} style={fieldStyle} />
          </div>
        </div>
        <div style={{ marginBottom:20 }}>
          <label htmlFor="client-registration-note" style={labelStyle}>Observação</label>
          <textarea id="client-registration-note" value={form.note} onChange={e=>setF("note",e.target.value)} rows={2} style={{ ...fieldStyle, resize:"vertical" }} />
          <VoiceDictationButton value={form.note} onChange={note => setF("note", note)} />
        </div>
        {saveError && <div role="alert" style={{ color:"#B91C1C", background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:8, padding:"9px 11px", marginBottom:12, fontSize:13 }}>{saveError}</div>}
        <button type="button" onClick={() => {
          if (submittedRef.current) return;
          if (liftOrderMode && !["lead", "porta"].includes(origemCliente)) return;
          if (!liftOrderMode
            && !canManuallyRegisterSale(currentUser)
            && salesStages.includes(form.stage)) {
            alert(MANUAL_SALE_CUTOFF_NOTICE);
            return;
          }
          if (!form.name.trim()) { alert("Informe o nome do cliente."); return; }
          submittedRef.current = true;
          setSaving(true);
          setSaveError("");
          const visitDate = locked
            ? (orderDate || form.visitDate)
            : (form.visitDate && form.visitDate <= today ? form.visitDate : today);
          const numVal = parseFloat(String(form.value).replace(",","."));
          const normalized = canonicalPhone(form.phone);
          const payload = {
            ...initialValues,
            ...form,
            ...(liftOrderMode ? { origemCliente } : {}),
            phone:normalized || form.phone.trim(),
            value:isNaN(numVal)?0:numVal,
            consultantId:finalConsultantId,
            storeId:finalStoreId,
            id:initialValues.id || clientIdentityKey({ storeId:finalStoreId, phone:normalized, name:form.name, product:form.product, createdAt:visitDate }),
            createdAt:liftOrderMode && Object.prototype.hasOwnProperty.call(initialValues, "createdAt")
              ? initialValues.createdAt
              : (initialValues.createdAt || visitDate),
            stageDates:{
              ...(initialValues.stageDates || {}),
              [form.stage]:visitDate,
            },
            ...(liftOrderMode ? {
              sellerId:finalConsultantId,
              visitDate,
              dataVenda:initialValues.dataVenda || visitDate,
              stageclosed:initialValues.stageclosed ?? (isStageClosed || form.stage === "closed"),
              ...(Object.prototype.hasOwnProperty.call(initialValues, "closedDate")
                ? { closedDate:initialValues.closedDate }
                : { closedDate:closedDate || visitDate }),
              ...(Object.prototype.hasOwnProperty.call(initialValues, "data_fechamento")
                ? { data_fechamento:initialValues.data_fechamento }
                : {}),
            } : {}),
          };
          Promise.resolve().then(() => onAdd(payload)).then(() => {
            if (!liftOrderMode) onClose();
          }).catch(error => {
            submittedRef.current = false;
            setSaving(false);
            setSaveError(error?.message || "Não foi possível salvar. Tente novamente.");
          });
        }} disabled={saving || (liftOrderMode && !["lead", "porta"].includes(origemCliente))} style={{ width:"100%", background:`linear-gradient(135deg,${T.accent},${T.accentLight})`, color:"#fff", border:"none", borderRadius:10, padding:"12px", fontWeight:700, fontSize:15, cursor:saving?"default":"pointer", opacity:saving || (liftOrderMode && !["lead", "porta"].includes(origemCliente))?0.7:1 }}>
          {saving ? "Salvando…" : (liftOrderMode ? "Salvar pedido LIFT" : "Adicionar Cliente")}
        </button>
      </div>
    </div>
  );
}

export default ClientRegistrationModal;